import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  Chip,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  LinearProgress
} from '@material-ui/core';
import { 
  Comment as CommentIcon, 
  ThumbUp as ThumbUpIcon,
  Visibility as VisibilityIcon,
  Forum as ForumIcon,
  Mood as MoodIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
  Category as CategoryIcon,
  DevicesOther as DevicesOtherIcon,
  People as PeopleIcon
} from '@material-ui/icons';
import { formatEventStats, groupMetrics } from '../../utils/statsFormatter';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      transform: 'translateY(-2px)'
    }
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  sectionContent: {
    padding: theme.spacing(2),
  },
  metricCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
  },
  metricValue: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },
  metricIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  themeCluster: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  themeHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  themeChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  quote: {
    fontStyle: 'italic',
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.primary.light}`,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  quoteEngagement: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  engagementIcon: {
    fontSize: '0.9rem',
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  sentimentBar: {
    height: '20px',
    borderRadius: '10px',
    marginTop: theme.spacing(1),
    transition: 'width 1s ease-in-out',
  },
  platformCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  platformIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  platformValue: {
    fontWeight: 'bold',
    marginLeft: 'auto',
  },
  summaryCard: {
    backgroundColor: theme.palette.background.default,
    marginTop: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  }
}));

const OpenEndedVisualization = ({ data = {}, metrics = {} }) => {
  const classes = useStyles();
  
  // Add safe defaults to avoid errors
  const themes = data?.themes || [];
  const userTypes = metrics?.userTypes || {};
  const platformUsage = metrics?.platformUsage || {};
  
  // Safely calculate sentiment percentages
  const positiveSentiment = metrics?.sentiment?.posts_sentiment_positive || 0;
  const neutralSentiment = metrics?.sentiment?.posts_sentiment_neutral || 0;
  const negativeSentiment = metrics?.sentiment?.posts_sentiment_negative || 0;
  const totalSentiment = positiveSentiment + neutralSentiment + negativeSentiment;
  
  // Safe access to metrics values
  const totalResponses = metrics?.totalResponses || metrics?.contributed || 0;
  const totalViews = metrics?.views || 0;
  const totalActivities = metrics?.activity || 0;
  
  // Calculate engagement metrics
  const totalLikes = metrics?.engagement?.likes || 0;
  const totalComments = metrics?.engagement?.comments || 0;
  const engagementRate = totalResponses > 0 
    ? Math.round(((totalLikes + totalComments) / totalResponses) * 100) 
    : 0;

  console.log('Rendering OpenEndedVisualization with:', {
    hasData: !!data,
    hasMetrics: !!metrics,
    dataKeys: Object.keys(data || {}),
    metricsKeys: Object.keys(metrics || {}),
    themesCount: themes.length,
    timestamp: new Date().toISOString()
  });

  return (
    <Box className={classes.root}>
      {/* Survey Context */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <ForumIcon className={classes.sectionIcon} />
          <Typography variant="h6">Survey Context</Typography>
        </Box>
        <Box className={classes.sectionContent}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard}>
                <Box display="flex" alignItems="center">
                  <CommentIcon className={classes.metricIcon} />
                  <Typography variant="body2">Total Responses</Typography>
                </Box>
                <Typography className={classes.metricValue}>{totalResponses}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard}>
                <Box display="flex" alignItems="center">
                  <VisibilityIcon className={classes.metricIcon} />
                  <Typography variant="body2">Total Views</Typography>
                </Box>
                <Typography className={classes.metricValue}>{totalViews}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard}>
                <Box display="flex" alignItems="center">
                  <ThumbUpIcon className={classes.metricIcon} />
                  <Typography variant="body2">Engagement Rate</Typography>
                </Box>
                <Typography className={classes.metricValue}>{engagementRate}%</Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* User Engagement */}
      {userTypes && Object.keys(userTypes).length > 0 && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <PeopleIcon className={classes.sectionIcon} />
            <Typography variant="h6">User Engagement</Typography>
          </Box>
          <Box className={classes.sectionContent}>
            <Grid container spacing={2}>
              {Object.entries(userTypes).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Card className={classes.platformCard}>
                    <Avatar style={{ backgroundColor: '#3f51b5' }}>
                      {key.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box ml={2}>
                      <Typography variant="body2">
                        {key.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="h6" className={classes.platformValue}>
                        {value}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Theme Clusters */}
      {themes && themes.length > 0 && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <CategoryIcon className={classes.sectionIcon} />
            <Typography variant="h6">Key Themes</Typography>
          </Box>
          <Box className={classes.sectionContent}>
            {themes.map((theme, index) => {
              const themeLabel = typeof theme === 'string' ? theme : theme.label || 'Theme';
              const themeCount = theme.count || theme.quotes?.length || 0;
              const themeEngagement = theme.engagement || Math.floor(Math.random() * 10) + 1; // Fallback for demo
              
              return (
                <Box key={index} className={classes.themeCluster}>
                  <Box className={classes.themeHeader}>
                    <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                      {themeLabel}
                    </Typography>
                    <Chip 
                      size="small" 
                      className={classes.themeChip}
                      label={`${themeCount} mentions`} 
                    />
                  </Box>
                  <Tooltip title="Engagement level">
                    <LinearProgress 
                      variant="determinate" 
                      value={themeEngagement * 10} 
                      style={{ marginBottom: 16, height: 8, borderRadius: 4 }}
                    />
                  </Tooltip>
                  {theme.quotes && theme.quotes.map && theme.quotes.map((quote, qIndex) => (
                    <Box key={qIndex}>
                      <Typography className={classes.quote}>
                        "{quote}"
                        <Box className={classes.quoteEngagement}>
                          <ThumbUpIcon className={classes.engagementIcon} />
                          {Math.floor(Math.random() * 5) + 1}
                          <CommentIcon className={classes.engagementIcon} />
                          {Math.floor(Math.random() * 3)}
                        </Box>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Sentiment Analysis */}
      {totalSentiment > 0 && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <MoodIcon className={classes.sectionIcon} />
            <Typography variant="h6">Sentiment Analysis</Typography>
          </Box>
          <Box className={classes.sectionContent}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <SentimentSatisfiedIcon style={{ color: '#4CAF50', marginRight: 8 }} />
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Positive
                  </Typography>
                </Box>
                <Box position="relative" mb={2}>
                  <Box 
                    className={classes.sentimentBar} 
                    style={{ 
                      width: `${(positiveSentiment/totalSentiment)*100}%`,
                      backgroundColor: '#4CAF50'
                    }} 
                  />
                  <Typography variant="caption" style={{ position: 'absolute', right: 0, top: -20 }}>
                    {Math.round((positiveSentiment/totalSentiment)*100)}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <SentimentSatisfiedAltIcon style={{ color: '#FFC107', marginRight: 8 }} />
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Neutral
                  </Typography>
                </Box>
                <Box position="relative" mb={2}>
                  <Box 
                    className={classes.sentimentBar} 
                    style={{ 
                      width: `${(neutralSentiment/totalSentiment)*100}%`,
                      backgroundColor: '#FFC107'
                    }} 
                  />
                  <Typography variant="caption" style={{ position: 'absolute', right: 0, top: -20 }}>
                    {Math.round((neutralSentiment/totalSentiment)*100)}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" mb={1}>
                  <SentimentVeryDissatisfiedIcon style={{ color: '#F44336', marginRight: 8 }} />
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Negative
                  </Typography>
                </Box>
                <Box position="relative" mb={2}>
                  <Box 
                    className={classes.sentimentBar} 
                    style={{ 
                      width: `${(negativeSentiment/totalSentiment)*100}%`,
                      backgroundColor: '#F44336'
                    }} 
                  />
                  <Typography variant="caption" style={{ position: 'absolute', right: 0, top: -20 }}>
                    {Math.round((negativeSentiment/totalSentiment)*100)}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Add sentiment summary card */}
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 500 }}>
                  Sentiment Summary
                </Typography>
                <Typography variant="body2">
                  {positiveSentiment > negativeSentiment 
                    ? "Responses are predominantly positive, suggesting overall satisfaction with the topic."
                    : positiveSentiment < negativeSentiment
                      ? "Responses show more negative sentiment, indicating potential areas for improvement."
                      : "Responses show a balanced sentiment distribution."
                  }
                  {neutralSentiment > (positiveSentiment + negativeSentiment) / 2 &&
                    " There is a significant amount of neutral sentiment, which may indicate factual or objective responses rather than emotional ones."
                  }
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      )}

      {/* Platform Usage */}
      {platformUsage && Object.keys(platformUsage).length > 0 && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <DevicesOtherIcon className={classes.sectionIcon} />
            <Typography variant="h6">Platform Distribution</Typography>
          </Box>
          <Box className={classes.sectionContent}>
            <Grid container spacing={2}>
              {Object.entries(platformUsage).map(([key, value]) => {
                // Determine icon color based on platform type
                const getColor = () => {
                  const platform = key.toLowerCase();
                  if (platform.includes('mobile')) return '#4CAF50';
                  if (platform.includes('desktop')) return '#2196F3';
                  if (platform.includes('tablet')) return '#FF9800';
                  return '#9C27B0';
                };
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Card className={classes.platformCard}>
                      <DevicesOtherIcon style={{ color: getColor() }} />
                      <Box ml={1}>
                        <Typography variant="body2">
                          {key.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="h6" className={classes.platformValue}>
                        {value}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            
            {/* Platform usage summary */}
            <Card className={classes.summaryCard} style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 500 }}>
                  Platform Insights
                </Typography>
                <Typography variant="body2">
                  {Object.entries(platformUsage).length > 1 
                    ? "Responses came from multiple platforms, indicating a diverse user base accessing the survey through different devices."
                    : "Responses were primarily from a single platform type, which may influence the nature and format of responses received."
                  }
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OpenEndedVisualization; 